import { RenderItemProps, VirtualList } from '@shared/components/ui/VirtualList';
import { useTranslation } from 'react-i18next';
import { Cart } from '../../models/Cart';
import { CartCard } from './CartCard';

// 🎯 Interface Segregation Principle (ISP) - Mismas props que CartTable
interface CartCardListProps {
  carts: Cart[];
  // 🎯 Acciones del dropdown (compartidas con tabla)
  onCartEdit?: (cart: Cart) => void;
  onCartDelete?: (cart: Cart) => void;
  onCartAdjust?: (cart: Cart) => void;
  onCartPrint?: (cart: Cart, size: 'small' | 'large') => void;
  onCartToggleLock?: (cart: Cart) => void;
  onCartViewProducts?: (cart: Cart) => void;
  isLoading?: boolean;
  className?: string;
  // 🎯 Props para scroll infinito
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting - manejado desde el padre
  sorting?: any;
  onSortingChange?: (sorting: any) => void;
  total?: number;
  // 🎯 Props específicas para cards
  showActions?: boolean;
  // 🎯 Prop para estimación de altura cuando es dinámica
  estimateItemHeight?: number;
}

export const CartCardList = ({
  carts,
  onCartEdit,
  onCartDelete,
  onCartAdjust,
  onCartPrint,
  onCartToggleLock,
  onCartViewProducts,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  sorting,
  onSortingChange,
  total,
  className = '',
  showActions = true,
  estimateItemHeight = 120, // 🎯 Estimación conservadora para cards con productos
}: CartCardListProps) => {
  const { t } = useTranslation('cartsModule');

  // 🎯 Render function que usa el componente CartCard
  const renderCartCard = (props: RenderItemProps<Cart>) => (
    <CartCard
      {...props}
      onEdit={onCartEdit}
      onDelete={onCartDelete}
      onAdjust={onCartAdjust}
      onPrint={onCartPrint}
      onToggleLock={onCartToggleLock}
      onViewProducts={onCartViewProducts}
      showActions={showActions}
    />
  );

  // 🎯 Key generator para optimizar renders
  const getCartKey = (cart: Cart) => {
    return cart.id;
  };

  return (
    <VirtualList
      data={carts}
      renderItem={renderCartCard}
      itemKey={getCartKey}
      // 🎯 Configuración optimizada para móvil con altura dinámica
      estimateItemHeight={estimateItemHeight} // 🎯 Solo estimación, altura real será medida
      height="calc(100vh - 150px)" // Altura completa en móvil
      gap={0} // Sin gap entre cards
      emptyMessage={t('empty.message')}
      className={`rounded-lg shadow-sm ${className}`}
      // 🎯 Props de scroll infinito
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      infiniteScrollThreshold={300} // Threshold mayor para móvil
      // 🎯 Props de sorting (aunque en móvil se use menos)
      sorting={sorting}
      onSortingChange={onSortingChange}
      total={total}
      // 🎯 Styling específico para móvil
      listClassName="touch-pan-y" // Mejor scroll en mobile
      itemClassName="touch-manipulation" // Optimización touch
    />
  );
};
